import React, { useEffect, useState, useContext, useRef } from "react";
import { Collapse, Checkbox, Card } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { TitleArea } from "../../../components/Typography/titleArea";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { StateContext } from "../../../store/context";

const { Panel } = Collapse;

const DEFAULT_PARAMETER_ID = 63; // Market Capitalization
const PARAMETER_STORAGE_KEY = "dynamic_graphs_selected_parameters";

interface Metric {
  id?: number;
  name: string;
  equation?: string;
  status?: number;
  type?: string;
  metric_category_id?: number;
  metric_category_name?: string;
  metric_order?: number;
  sortableId: any;
}

interface MetricsGroup {
  [key: string]: Metric[];
}

interface ParameterSelectorProps {
  metricsAndEquations: MetricsGroup;
  onChange?: (selectedParameters: { name: string }[]) => void;
  selectedMetrics: any;
}

interface SortableItemProps {
  metric: Metric;
  sortableId: string;
  isChecked: boolean;
  onCheckboxChange: (metric: Metric, e: CheckboxChangeEvent) => void;
  disabled: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({
  metric,
  sortableId,
  isChecked,
  onCheckboxChange,
  disabled,
}) => {
  const { authUser } = useContext(StateContext);
  const { role_name, sub_plan } = authUser;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [isHovered, setIsHovered] = useState(false);

  const formattedName = metric.name.replace(/_/g, " ");
  const isBasicCustomer =
    sub_plan === "Free Plan" && role_name === "Basic Customer";

  const isDisableSortableId =
    // sortableId.includes("Investor Ratio") ||
    sortableId.includes("Efficiency") ||
    sortableId.includes("Income Statement") ||
    sortableId.includes("Cash Flow");

  return (
    <div ref={setNodeRef} style={style} className="flex items-center p-3 ">
      <span
        {...attributes}
        {...listeners}
        className="mr-3 text-[color:var(--vi-text-secondary)] cursor-grab !font-inter font-normal"
      >
        <MenuOutlined />
      </span>
      <div className="flex gap-x-2 items-center w-full relative">
        <Checkbox
          checked={isChecked}
          onChange={(e) => onCheckboxChange(metric, e)}
          disabled={disabled && !isChecked}
        />
        <span
          className={`text-sm text-[color:var(--vi-text-secondary)] !font-inter font-normal !capitalize truncate ${
            disabled && !isChecked ? "text-[color:var(--vi-text-tertiary)]" : ""
          }`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {formattedName}
        </span>
        {isHovered && (
          <div className="vi-glass-card absolute top-full left-0 mt-1 p-2 z-10">
            <span className="text-sm text-[color:var(--vi-text)] !font-inter font-normal whitespace-nowrap">
              {formattedName}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const ParameterSelector: React.FC<ParameterSelectorProps> = ({
  metricsAndEquations,
  onChange,
  selectedMetrics,
}) => {
  const [groups, setGroups] = useState<MetricsGroup>({});
  const maxSelections = 5;
  const hasRestoredParams = useRef(false);

  useEffect(() => {
    const initialGroups: MetricsGroup = {};

    if (metricsAndEquations && typeof metricsAndEquations === "object") {
      Object.entries(metricsAndEquations).forEach(([category, metrics]) => {
        if (Array.isArray(metrics)) {
          initialGroups[category] = metrics
            .filter(
              (metric: any) =>
                metric.is_visible === undefined || metric.is_visible === 1,
            )
            .map((metric, index) => ({
              ...metric,
              sortableId: `${category}-${index}`,
            }));
        }
      });
    }

    setGroups(initialGroups);
  }, [metricsAndEquations]);

  // Restore previous parameter selection, or fall back to the default, once groups load
  useEffect(() => {
    if (Object.keys(groups).length === 0 || hasRestoredParams.current) return;
    hasRestoredParams.current = true;

    if (selectedMetrics.length > 0) return; // already has a selection, don't override

    const allMetrics = Object.values(groups).flat();

    try {
      const saved = localStorage.getItem(PARAMETER_STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : null;

      if (parsed?.cleared) {
        // User explicitly cleared last time — respect it
        return;
      }

      const savedNames: string[] = parsed?.names || [];
      const savedMetrics = allMetrics.filter((m) =>
        savedNames.includes(m.name),
      );

      if (savedMetrics.length > 0) {
        onChange?.(savedMetrics);
        return;
      }
    } catch {
      // ignore parse errors
    }

    const defaultMetric = allMetrics.find((m) => m.id === DEFAULT_PARAMETER_ID);
    if (defaultMetric) {
      onChange?.([defaultMetric]);
    }
  }, [groups]);

  // Persist the selection whenever it changes
  useEffect(() => {
    if (!hasRestoredParams.current) return; // don't persist before restore has run

    if (selectedMetrics.length > 0) {
      localStorage.setItem(
        PARAMETER_STORAGE_KEY,
        JSON.stringify({
          names: selectedMetrics.map((m: any) => m.name),
        }),
      );
    } else {
      localStorage.setItem(
        PARAMETER_STORAGE_KEY,
        JSON.stringify({ cleared: true }),
      );
    }
  }, [selectedMetrics]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleCheckboxChange = (metric: Metric, e: CheckboxChangeEvent) => {
    if (e.target.checked && selectedMetrics.length >= maxSelections) {
      return;
    }

    const newSelectedParams = e.target.checked
      ? [...selectedMetrics, metric]
      : selectedMetrics.filter((p: any) => p.name !== metric.name);

    onChange?.(newSelectedParams);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = active.id.toString();
    const overId = over.id.toString();

    const activeCategory = Object.entries(groups).find(([_, metrics]) =>
      metrics.some((m) => m.sortableId === activeId),
    )?.[0];

    const overCategory = Object.entries(groups).find(([_, metrics]) =>
      metrics.some((m) => m.sortableId === overId),
    )?.[0];

    if (activeCategory && overCategory && activeCategory === overCategory) {
      const items = [...groups[activeCategory]];
      const oldIndex = items.findIndex((m) => m.sortableId === activeId);
      const newIndex = items.findIndex((m) => m.sortableId === overId);

      setGroups({
        ...groups,
        [activeCategory]: arrayMove(items, oldIndex, newIndex),
      });
    }
  };

  // Set default open panel to "Efficiency"
  const defaultActiveKey = Object.keys(groups).find((key) =>
    key.toLowerCase().includes("efficiency"),
  );
  return (
    <div className="!w-full h-full md:!h-[98vh] overflow-y-auto lg:px-2 pt-4 pb-2 md:pb-10">
      <div className="vi-dg-param-title hidden lg:block mb-2">
        <TitleArea title="Parameter" />
      </div>
      {/* <div className="text-sm text-gray-500 mb-2 px-4">
        Select up to {maxSelections} parameters
      </div> */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Collapse
          defaultActiveKey={"5-Efficiency"}
          expandIconPosition="end"
          className="!border-none !text-[color:var(--vi-text-secondary)] !font-inter !font-normal"
          expandIcon={({ isActive }) =>
            isActive ? (
              <FaChevronUp className="text-[color:var(--vi-text-tertiary)]" />
            ) : (
              <FaChevronDown className="text-[color:var(--vi-text-tertiary)]" />
            )
          }
        >
          {Object.entries(groups).map(([category, metrics]) => (
            <Panel
              header={category.split("-")[1]}
              key={category}
              className="bg-[#F0F6FF] text-[color:var(--vi-text)]"
            >
              <SortableContext
                items={metrics.map((m) => m.sortableId)}
                strategy={verticalListSortingStrategy}
              >
                {metrics.map((metric) => {
                  return (
                    <SortableItem
                      key={metric.sortableId}
                      sortableId={metric.sortableId}
                      metric={metric}
                      isChecked={selectedMetrics?.some(
                        (p: any) => p.name === metric.name,
                      )}
                      onCheckboxChange={handleCheckboxChange}
                      disabled={selectedMetrics.length >= maxSelections}
                    />
                  );
                })}
              </SortableContext>
            </Panel>
          ))}
        </Collapse>
      </DndContext>
    </div>
  );
};

export default ParameterSelector;
